import React, { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import {
    Button, Card, Checkbox, DatePicker, Form, Input, Select,
    Typography, Switch, Radio, InputNumber, Row, Col, Tag, message
} from "antd"
import { MinusCircleOutlined, PlusOutlined, CarOutlined, ToolOutlined, InfoCircleOutlined, AppstoreOutlined } from "@ant-design/icons"

// Components
import showSecondarySidebar from "../../../../layout/MainLayout/helpers/showSecondarySidebar"
import Page from "../../../../components/Decoration/Page"

// Services Library
import { TIME, REDUX } from "../../../../services/util"

// Redux Actions
import * as REDUX_ACTIONS from '../../../../services/redux/actions'

const { Text } = Typography

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputStyle  = { borderRadius: 8, borderColor: '#dde1ee', background: '#fafbff' }
const cardStyle   = { borderRadius: 12, border: '1px solid #e8eaf0', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }
const cardHeadStyle = { fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: '#8c93b0', textTransform: 'uppercase', borderBottom: '1.5px solid #e8eaf0', minHeight: 44 }

// ─── Form Label ───────────────────────────────────────────────────────────────
const Lbl = ({ required, children }) => (
    <span style={{ fontSize: 12, fontWeight: 600, color: '#555d7a' }}>
        {required && <span style={{ color: '#ff4d4f', marginRight: 3 }}>*</span>}
        {children}
    </span>
)

const ACCESSORIES = ['Buzzer', 'RFID', 'Intercom', 'Immobilizer']

const VehicleCUForm2 = ({ data = {} }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [form]   = Form.useForm()

    const user           = useSelector(REDUX.reduxStoreWrapper('user'))
    const vehicleGroups  = useSelector(REDUX.reduxStoreWrapper('vehicleGroups'))
    const devicesMDVR    = useSelector(REDUX.reduxStoreWrapper('devicesMDVR.byID'))
    const devices        = useSelector(REDUX.reduxStoreWrapper('devices.staticsByID'))
    const vehicles       = useSelector(REDUX.reduxStoreWrapper('vehicles.staticsByID'))
    const ruleTemplates  = useSelector(REDUX.reduxStoreWrapper('ruleTemplates.byID'))
    const vehiclePackage = useSelector(REDUX.reduxStoreWrapper('subscriptionPackages.vehiclePKGByID'))
    const allVPKGIDs     = useSelector(REDUX.reduxStoreWrapper('subscriptionPackages.allVehiclePKGIDs'))
    const users          = useSelector(REDUX.reduxStoreWrapper('userHierarchy.byID'))
    const loading        = useSelector(REDUX.reduxStoreWrapper('style.loading.vehicle'))

    const vehiclesSet    = new Set(Object.values(vehicles).map(v => v.dvid?.split('#')[1]).filter(v => v && v !== (data?.dvid?.split('#')[1] || data.imei)))
    const devicesMDVRSet = new Set(Object.values(vehicles).map(d => d.mdvrid))

    const { uid, uroleid } = user

    const [selectedAllVPKGID, setSelectedAllVPKGID] = useState(data.vpkgids || ['ALL'])
    const [selectedAllVGID,   setSelectedAllVGID]   = useState(data.vhcgids || [])

    const sensorTypeOptions = ['Canbus', 'Canbus+', 'Dipstick', 'Hardwiring']
    const typeOptions = ['Bus', 'Four Wheel Drive', 'Motocar', 'Motocycle', 'Taxi', 'Truck', 'Van', 'Others']

    const operatorOptions2 = { 
        canbus: ['%', 'L'], 
        'canbus+': ['%'],
        dipstick: ['V', '%'], 
        hardwiring: ['V', '%'] 
    }
    const deviceParamOptions = { 
        '%': 'ecu_can_fuel_lvl_pct',
        'L': 'ecu_can_fuel_lvl_ltr', 
        'V': 'ai' 
    }

    const ruleTemplateOptions = Object.values(ruleTemplates).map(rt => ({ label: rt.name, value: rt.ruletid }))

    const vehiclePackageOptions = [
        { label: 'ALL', value: 'ALL' },
        ...Object.values(vehiclePackage).map(vp => ({ label: vp.name, value: vp.vpkgid }))
    ]?.map(vp => {
        form.setFieldsValue({ vpkgids: selectedAllVPKGID.includes('ALL') ? ['ALL'] : selectedAllVPKGID })
        return { label: vp.label, value: vp.value, disabled: selectedAllVPKGID.includes('ALL') && vp.value !== 'ALL' }
    })

    const vehicleGroupOptions = vehicleGroups?.relationByUID[uid]
        ?.map(vg => vehicleGroups.byID[vg] || {})
        ?.filter(vg => (vg.status === 1) || (vg.name === 'ALL'))
        ?.map(vg => ({
            label: vg.name, value: vg.vhcgid,
            disabled: selectedAllVGID.includes(`vhcg#${uid || ''}`) && vg.vhcgid !== `vhcg#${uid || ''}`
        }))

    const deviceOptions = Object.values(devices)
        ?.filter(d => d.status === 1 && !vehiclesSet.has(d.dvid.split('#')[1]))
        ?.map(d => ({ label: d.dvid?.split('#')[1], value: d.dvid }))

    const deviceMDVROptions = Object.values(devicesMDVR)
        ?.filter(d => d.status === 1 && !devicesMDVRSet.has(d.mdvrid))
        ?.map(d => ({ label: d.mdvrid, value: d.mdvrid }))

    const title = data?.title || 'Create'
    const ai    = useRef(0)

    const onFinish = (e) => {
        const nfuel_profile = e.fuel_profile?.map((fp, i) => {
            const deviceParam = `${i}_deviceParam`

            if(fp[`${i}_sensorType`] === 'canbus+') fp[`${i}_deviceParam`] = 'ecu_fms_fuel_lvl_pct'
            if((fp[deviceParam] || '')?.includes('ai')) fp[`${i}_deviceParam`] = `ai_${ai.current++}`

            // if ((fp[deviceParam] || '')?.includes('ai')) ai.current += 1

            return fp

            // return { 
            //     ...fp, 
            //     [`${i}_deviceParam`]: (fp[deviceParam] || '')?.includes('ai') ? `ai_${ai.current}` : fp[deviceParam] 
            // }
        })

        const fuel_profile = nfuel_profile && Object.assign({}, ...nfuel_profile) || {}

        const items = {
            vehicle: {
                vid: data.vid, 
                status: data.status || 1,
                plate: e.plate, 
                displayName: e.displayName, 
                brand: e.brand, 
                model: e.model, 
                type: e.type,
                registeredAt: TIME.parseTimestamp(e.registeredAt) || 0,
                chassisNumber: e.chassisNumber, 
                engineNumber: e.engineNumber,
                fuel_type: e.fuel_type || '', 
                loadedWeight: e.loadedWeight, 
                unloadedWeight: e.unloadedWeight,
                engineCC: 0, 
                imei: e.dvid?.split('#')[1], 
                dvid: e.dvid, 
                mdvrid: e.mdvrid
            },
            ...e.fuel_sensor && { 
                fuelProfile: { 
                    tankNum: e.fuel_profile?.length || 1, 
                    ...fuel_profile 
                } 
            },
            imei: e.dvid?.split('#')[1],
            vpkgids: e.vpkgids?.includes('ALL') ? allVPKGIDs : e.vpkgids,
            vhcgids: e.vhcgids, 
            company: e.company, 
            ruletids: e.ruletids, 
            uid: uid
        }

        switch (title) {
            case 'Create': 
                dispatch(REDUX_ACTIONS.v3_vehicles.create_vehicle_request(items, () => navigate('/management/overall')))
                break
            case 'Edit':   
                dispatch(REDUX_ACTIONS.v3_vehicles.update_vehicle_request(items, () => navigate('/management/overall')))
                break
            default: break
        }
    }

    const [gps,        setGPS]        = useState(data?.gnss_status || data?.imei)
    const [mdvr,       setMDVR]       = useState(data?.mdvrid)
    const [fuelSensor, setFuelSensor] = useState(data?.fuel_sensor)

    // Kembalikan operatorOption function
    const operatorOption = (param) => operatorOptions2[param]?.map(opt => ({ value: opt, label: opt }))

    // Kembalikan logic grouped untuk fuel_profile
    const grouped = Object.entries(data.fuelProfile || {}).reduce((acc, [key, value]) => {
        const match = key.match(/^(\d+)_/)
        if (match) {
            const index = match[1]
            acc[index] = acc[index] || {}
            acc[index][key] = typeof value === 'string' && (value || '')?.includes('ai_') ? 'ai' : value
        }
        return acc
    }, {})

    const fuel_profile_array = Object.values(grouped).length ? Object.values(grouped) : [{}]

    // Kembalikan state untuk operator dan sensorType per fuel profile
    const opinit = fuel_profile_array.map((fp, i) => fp[`${i}_operator`]) || []
    const stinit = fuel_profile_array.map((fp, i) => fp[`${i}_sensorType`]) || []
    const [op, setOp] = useState(opinit)
    const [st, setSt] = useState(stinit)

    useEffect(() => {
        if (data?.ruletids && user.uid) {
            dispatch(REDUX_ACTIONS.v3_ruleTemplates.get_rule_templates_request(data.ruletids, user.uid))
        }
    }, [data, user])

    useEffect(() => {
        form.setFieldsValue({
            vhcgids:  data?.vhcgids  || [`vhcg#${uid || ''}`],
            ruletids: data.ruletids  || [`rulet#${uid || ''}`],
            company:  data?.company  || user.uid
        })
        setSelectedAllVGID(data?.vhcgids || [`vhcg#${uid || ''}`])
    }, [uid])

    return (
        <div className="page-container">
            <Page title={`${title} Vehicle`}>
                <Form
                    style={{ width: '100%', maxWidth: 860, margin: 'auto', padding: '0 8px' }}
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={() => message.error('Please fill the required fields!')}
                    autoComplete="off"
                    initialValues={{
                        ...data,
                        gnss_status:  data.gnss_status || data.imei,
                        registeredAt: TIME.dayJS(data?.registeredAt),
                        fuel_profile: fuel_profile_array || [{}],
                        vpkgids:      data?.vpkgids || ['ALL'] || [],
                        mdvr:         data?.mdvrid,
                    }}
                >
                    {/* ══ GENERAL INFORMATION ══════════════════════════════════════════ */}
                    <Card
                        title={<><InfoCircleOutlined style={{ color: '#4f6ef7', marginRight: 8 }} />General Information</>}
                        style={cardStyle}
                        headStyle={cardHeadStyle}
                    >
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item label={'Company Name'} name="company" rules={[{ required: true, message: 'Please select company name!' }]}>
                                    <Select size="large" style={inputStyle} options={Object.values(users).map(u => ({ label: u.name, value: u.uid }))} disabled />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item label={'Vehicle Plate'} name="plate" rules={[{ required: true, message: 'Please input vehicle plate!' }]}>
                                    <Input size="large" style={inputStyle} placeholder="e.g. B 1234 ABC" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label={'Vehicle Name'} name="displayName" rules={[{ required: true, message: 'Please input vehicle name!' }]}>
                                    <Input size="large" style={inputStyle} placeholder="e.g. Truck 01" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item label={'Type'} name="type" rules={[{ required: true, message: 'Please select vehicle type!' }]}>
                                    <Select size="large" style={inputStyle} placeholder="Select type" options={typeOptions.map(m => ({ value: m, label: m }))} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label={'Brand'} name="brand" rules={[{ required: true, message: 'Please input vehicle brand!' }]}>
                                    <Input size="large" style={inputStyle} placeholder="e.g. Toyota" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item label={'Model'} name="model" rules={[{ required: true, message: 'Please input vehicle model!' }]}>
                                    <Input size="large" style={inputStyle} placeholder="e.g. Hilux" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label={'Registration Date'} name="registeredAt" rules={[{ required: true, message: 'Please input registration date!' }]}>
                                    <DatePicker size="large" style={{ width: '100%', ...inputStyle }} format="YYYY-MM-DD" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item label={'Vehicle Groups'} name="vhcgids" rules={[{ required: true, message: 'Please select vehicle groups!' }]}>
                                    <Select
                                        mode="multiple" size="large" style={inputStyle}
                                        options={vehicleGroupOptions} showSearch placeholder="Select Vehicle Groups"
                                        onChange={e => setSelectedAllVGID(e)}
                                        filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                        tagRender={({ value, label, closable, onClose }) => (
                                            <Tag color={vehicleGroups.byID[value]?.color} closable={closable} onClose={onClose}>{label}</Tag>
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label={'Rule Template'} name="ruletids" rules={[{ required: true, message: 'Please select rule template!' }]}>
                                    <Select mode="multiple" size="large" style={inputStyle} placeholder="Select Rule Template" options={ruleTemplateOptions} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item label={'Vehicle Package'} name="vpkgids" rules={[{ required: true, message: 'Please select vehicle package!' }]}>
                                    <Select
                                        size="large" mode="multiple" showSearch style={inputStyle}
                                        options={vehiclePackageOptions} onChange={e => setSelectedAllVPKGID(e)}
                                        placeholder="Select Vehicle Package"
                                        filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    {/* ══ DEVICE INFORMATION ═══════════════════════════════════════════ */}
                    <Card
                        title={<><ToolOutlined style={{ color: '#4f6ef7', marginRight: 8 }} />Device Information</>}
                        style={cardStyle}
                        headStyle={cardHeadStyle}
                    >
                        {/* GPS Tracker */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <Form.Item name="gnss_status" noStyle>
                                <Switch
                                    disabled={!((uroleid || '').includes('D') || uroleid === 'A')}
                                    value={gps}
                                    onChange={e => setGPS(e)}
                                />
                            </Form.Item>
                            <Text style={{ fontWeight: 600, color: '#333' }}>GPS Tracker</Text>
                        </div>
                        {gps && (
                            <Row gutter={16} style={{ marginBottom: 8 }}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={<Lbl>Device IMEI</Lbl>} name="dvid">
                                        <Select
                                            showSearch size="large" style={inputStyle} placeholder="Select Device IMEI"
                                            options={deviceOptions}
                                            disabled={!((uroleid || '').includes('D') || uroleid === 'A')}
                                            filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={<Lbl>GPS Brand</Lbl>} name="gpsBrand">
                                        <Input size="large" style={inputStyle} placeholder="e.g. Teltonika" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}

                        <div style={{ borderTop: '1px dashed #e8eaf0', margin: '8px 0 16px' }} />

                        {/* MDVR */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <Form.Item name="mdvr" noStyle>
                                <Switch
                                    disabled={!((uroleid || '').includes('D') || uroleid === 'A')}
                                    value={mdvr}
                                    onChange={e => setMDVR(e)}
                                />
                            </Form.Item>
                            <Text style={{ fontWeight: 600, color: '#333' }}>MDVR</Text>
                        </div>
                        {mdvr && (
                            <Row gutter={16} style={{ marginBottom: 8 }}>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={<Lbl>MDVR Device</Lbl>} name="mdvrid">
                                        <Select
                                            showSearch size="large" style={inputStyle} placeholder="Select MDVR Device"
                                            options={deviceMDVROptions}
                                            disabled={!((uroleid || '').includes('D') || uroleid === 'A')}
                                            filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}

                        <div style={{ borderTop: '1px dashed #e8eaf0', margin: '8px 0 16px' }} />

                        {/* Fuel Sensor */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <Form.Item name="fuel_sensor" noStyle>
                                <Switch value={fuelSensor} onChange={e => setFuelSensor(e)} />
                            </Form.Item>
                            <Text style={{ fontWeight: 600, color: '#333' }}>Fuel Sensor</Text>
                        </div>

                        {fuelSensor && (
                            <>
                                <Form.Item label={<Lbl>Fuel Profile</Lbl>} style={{ marginBottom: 0 }}>
                                    <Form.List name="fuel_profile">
                                        {(fields, opt) => (
                                            <div>
                                                {fields.map((field, i) => (
                                                    <div key={i} style={{
                                                        border: '1px solid #e0e4f4',
                                                        borderRadius: 10,
                                                        padding: '14px 16px',
                                                        marginBottom: 12,
                                                        background: '#f5f7ff'
                                                    }}>
                                                        <div style={{ marginBottom: 10 }}>
                                                            <Lbl required>Fuel Sensor Type</Lbl>
                                                            <Form.Item 
                                                                name={[field.name, `${i}_sensorType`]} 
                                                                noStyle 
                                                                rules={[{ required: true, message: 'Please select fuel sensor type!' }]}
                                                            >
                                                                <Select 
                                                                    style={{ width: '100%', marginTop: 4 }}
                                                                    placeholder="Select sensor type"
                                                                    options={sensorTypeOptions.map(o => ({ 
                                                                        value: o.toLowerCase(), 
                                                                        label: o 
                                                                    }))}
                                                                    onChange={(e) => {
                                                                        const fuel_profile = form.getFieldValue('fuel_profile')
                                                                        const operator = operatorOptions2[e]

                                                                        // Update operator dan sensorType state
                                                                        const newOp = [...op]
                                                                        const newSt = [...st]
                                                                        newOp[i] = operator
                                                                        newSt[i] = e
                                                                        
                                                                        setOp(newOp)
                                                                        setSt(newSt)

                                                                        // Update form values
                                                                        form.setFieldsValue({ 
                                                                            fuel_profile: fuel_profile.map((fp, ic) => 
                                                                                ic === i ? { 
                                                                                    ...fp, 
                                                                                    [`${i}_model`]: e?.toUpperCase() 
                                                                                } : fp 
                                                                            )
                                                                        })
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </div>

                                                        <Row gutter={16}>
                                                            <Col xs={24} sm={12}>
                                                                <div style={{ marginBottom: 10 }}>
                                                                    <Lbl required>Fuel Capacity (L)</Lbl>
                                                                    <Form.Item 
                                                                        name={[field.name, `${i}_capacity`]} 
                                                                        noStyle 
                                                                        rules={[{ required: true, message: 'Please input fuel capacity!' }]}
                                                                    >
                                                                        <InputNumber style={{ width: '100%', marginTop: 4 }} placeholder="e.g. 200" />
                                                                    </Form.Item>
                                                                </div>
                                                            </Col>
                                                            <Col xs={24} sm={12}>
                                                                <div style={{ marginBottom: 10 }}>
                                                                    <Lbl required>Fuel Level Operator</Lbl>
                                                                    <Form.Item 
                                                                        name={[field.name, `${i}_operator`]} 
                                                                        noStyle 
                                                                        rules={[{ required: true, message: 'Please select operator!' }]}
                                                                    >
                                                                        <Select
                                                                            style={{ width: '100%', marginTop: 4 }}
                                                                            placeholder="Select operator"
                                                                            options={operatorOption(st[i])}
                                                                            onChange={e => {
                                                                                const fp = form.getFieldValue('fuel_profile')
                                                                                const deviceParam = deviceParamOptions[e]
                                                                                
                                                                                form.setFieldsValue({
                                                                                    fuel_profile: fp.map((f, ic) =>
                                                                                        ic === i ? { 
                                                                                            ...f, 
                                                                                            [`${i}_deviceParam`]: deviceParam 
                                                                                        } : f
                                                                                    )
                                                                                })
                                                                            }}
                                                                        />
                                                                    </Form.Item>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                        <Row gutter={16}>
                                                            <Col xs={24} sm={12}>
                                                                <div style={{ marginBottom: 10 }}>
                                                                    <Lbl required>Fuel Maximum Level</Lbl>
                                                                    <Form.Item 
                                                                        name={[field.name, `${i}_max`]} 
                                                                        noStyle 
                                                                        rules={[{ required: true, message: 'Please input max level!' }]}
                                                                    >
                                                                        <InputNumber style={{ width: '100%', marginTop: 4 }} placeholder="Max level" />
                                                                    </Form.Item>
                                                                </div>
                                                            </Col>
                                                            <Col xs={24} sm={12}>
                                                                <div style={{ marginBottom: 10 }}>
                                                                    <Lbl required>Fuel Minimum Level</Lbl>
                                                                    <Form.Item 
                                                                        name={[field.name, `${i}_min`]} 
                                                                        noStyle 
                                                                        rules={[{ required: true, message: 'Please input min level!' }]}
                                                                    >
                                                                        <InputNumber style={{ width: '100%', marginTop: 4 }} placeholder="Min level" />
                                                                    </Form.Item>
                                                                </div>
                                                            </Col>
                                                        </Row>

                                                        {/* Fuel Type + actions inline */}
                                                        <div style={{
                                                            display: 'flex', alignItems: 'center',
                                                            flexWrap: 'wrap', gap: 8,
                                                            paddingTop: 10, borderTop: '1px dashed #dde1ee'
                                                        }}>
                                                            <Text style={{ fontSize: 12, fontWeight: 600, color: '#555d7a', marginRight: 4 }}>Fuel Type:</Text>
                                                            <Form.Item name={[field.name, `${i}_fuel_type`]} noStyle>
                                                                <Radio.Group size="small">
                                                                    {['ron_97','ron_95','ron_100','diesel','natural_gas','battery'].map(v => (
                                                                        <Radio.Button key={v} value={v} style={{ borderRadius: 6, fontSize: 12 }}>
                                                                            {v === 'ron_97' ? 'RON 97' : v === 'ron_95' ? 'RON 95' : v === 'ron_100' ? 'RON 100' : v === 'natural_gas' ? 'Natural Gas' : v.charAt(0).toUpperCase() + v.slice(1)}
                                                                        </Radio.Button>
                                                                    ))}
                                                                </Radio.Group>
                                                            </Form.Item>
                                                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                                                <Button
                                                                    size="small" danger type="default"
                                                                    icon={<MinusCircleOutlined />}
                                                                    onClick={() => opt.remove(field.name)}
                                                                    style={{ borderRadius: 6 }}
                                                                >
                                                                    Remove
                                                                </Button>
                                                                <Button
                                                                    size="small" type="dashed"
                                                                    icon={<PlusOutlined />}
                                                                    onClick={() => opt.add()}
                                                                    style={{ borderRadius: 6 }}
                                                                >
                                                                    Add Fuel Profile
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {(fields.length === 0) && (
                                                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => opt.add()} style={{ borderRadius: 8 }}>
                                                        Add Fuel Profile
                                                    </Button>
                                                ) || ''}
                                            </div>
                                        )}
                                    </Form.List>
                                </Form.Item>
                            </>
                            ||
                            ''
                        ) || ''}
                    </Card>

                    {/* ══ ACCESSORIES / PERIPHERALS ════════════════════════════════════ */}
                    <Card
                        title={<><AppstoreOutlined style={{ color: '#4f6ef7', marginRight: 8 }} />Accessories / Peripherals</>}
                        style={cardStyle}
                        headStyle={cardHeadStyle}
                    >
                        <Form.Item name="accessories" noStyle>
                            <Checkbox.Group>
                                <Row gutter={[12, 8]}>
                                    {ACCESSORIES.map(acc => (
                                        <Col key={acc}>
                                            <Checkbox value={acc.toLowerCase()}>
                                                <Tag style={{
                                                    borderRadius: 20, padding: '2px 12px',
                                                    fontSize: 13, fontWeight: 500,
                                                    border: '1px solid #dde1ee',
                                                    background: '#f0f2ff', color: '#4f6ef7',
                                                    cursor: 'pointer', margin: 0
                                                }}>
                                                    {acc}
                                                </Tag>
                                            </Checkbox>
                                        </Col>
                                    ))}
                                </Row>
                            </Checkbox.Group>
                        </Form.Item>
                    </Card>

                    {/* ══ DETAIL INFORMATION ═══════════════════════════════════════════ */}
                    <Card
                        title={<><CarOutlined style={{ color: '#4f6ef7', marginRight: 8 }} />Detail Information</>}
                        style={cardStyle}
                        headStyle={cardHeadStyle}
                    >
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item label={<Lbl>Vehicle Chassis No</Lbl>} name="chasisNumber">
                                    <Input size="large" style={inputStyle} placeholder="Enter chassis number" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label={<Lbl>Vehicle Engine No</Lbl>} name="engineNumber">
                                    <Input size="large" style={inputStyle} placeholder="Enter engine number" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item label={<Lbl>Unloaded Weight</Lbl>} name="unloadedWeight">
                                    <InputNumber size="large" style={{ width: '100%', ...inputStyle }} placeholder="kg" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label={<Lbl>Loaded Weight</Lbl>} name="loadedWeight">
                                    <InputNumber size="large" style={{ width: '100%', ...inputStyle }} placeholder="kg" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item label={<Lbl>Year of Manufacture</Lbl>} name="yearOfManufacture">
                                    <InputNumber size="large" style={{ width: '100%', ...inputStyle }} placeholder="e.g. 2022" min={1900} max={2100} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label={<Lbl>Color</Lbl>} name="color">
                                    <Input size="large" style={inputStyle} placeholder="e.g. White" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item label={<Lbl>Seat Capacity</Lbl>} name="seatCapacity">
                                    <InputNumber size="large" style={{ width: '100%', ...inputStyle }} placeholder="Number of seats" min={1} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item label={<Lbl>Remarks</Lbl>} name="remarks">
                                    <Input size="large" style={inputStyle} placeholder="Additional notes" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    {/* ══ ACTION BUTTONS ═══════════════════════════════════════════════ */}
                    <div style={{
                        display: 'flex', justifyContent: 'flex-end', gap: 12,
                        marginTop: 8, paddingTop: 20, borderTop: '1px solid #e8eaf0'
                    }}>
                        <Button
                            size="large" onClick={() => navigate(-1)}
                            style={{ borderRadius: 8, minWidth: 100, borderColor: '#d9d9d9', color: '#666' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="large" type="primary" htmlType="submit" loading={loading}
                            style={{ borderRadius: 8, minWidth: 120 }}
                        >
                            {title === 'Create' ? 'Add Vehicle' : 'Save Changes'}
                        </Button>
                    </div>
                </Form>
            </Page>
        </div>
    )
}

const MemoizedVehicleCUForm2 = React.memo(VehicleCUForm2)

export default showSecondarySidebar(false)(MemoizedVehicleCUForm2)